import * as Y from "yjs";
import { IndexeddbPersistence } from "y-indexeddb";
import YProvider from "y-partyserver/provider";
import { removeAwarenessStates } from "y-protocols/awareness";
import { PlanStore } from "@mcp/plan";
import { serverHost, serverProtocol } from "../lib/share.ts";

/** idle = still live, but the connection is paused while nobody uses this tab (see IDLE) */
export type CollabStatus = "offline" | "connecting" | "connected" | "error" | "ended" | "idle";
/**
 * local   – saved in this browser (not live)
 * syncing – live, catching up with the server
 * synced  – saved in this browser and on the server
 * offline – live session lost its connection; changes are kept here and sync when it's back
 */
export type SaveState = "local" | "syncing" | "synced" | "offline";

export interface Peer {
  clientId: number;
  name: string;
  color: string;
  /** account id of the person (shared worlds of accounts): matches them to the People list */
  uid?: string;
  cursor: { x: number; y: number; z: number } | null;
}

/** PlanStore methods that change the plan — a view-only session turns them into no-ops (see PlanSession.readOnly) */
const WRITES = new Set([
  "create", "setHome", "setDefaultColor", "rememberColor", "createGroup", "saveTemplate", "deleteTemplate", "rememberMaterial",
  "updateMany", "removeMany", "duplicate", "update", "remove", "addCard", "updateCard", "removeCard", "raiseCard",
  "loadFile", "importJson", "saveVersion", "deleteVersion", "renameVersion", "restoreVersion",
]);

/** How to get into a live room: a ticket per connect (shared worlds of accounts) or the host key (worlds shared before). */
export interface LiveRoom {
  room: string;
  /** fresh 60-second room ticket (lib/collab.ts) — asked again on every reconnect */
  ticket?: () => Promise<string>;
  /** legacy rooms: only the host passes it (end session, reopen) */
  hostKey?: string | null;
}

/** why the server closed our connection for good (role-changed and plan-changed only reconnect) */
export type KickReason = "removed" | "left" | "deleted" | "claimed" | "no-access";

const COLORS = ["#e6194b", "#3cb44b", "#4363d8", "#f58231", "#911eb4", "#46f0f0", "#f032e6", "#bcf60c", "#fabebe", "#008080"];

/** Cursor updates are throttled: every websocket message counts against the free Durable Object quota. */
const CURSOR_INTERVAL_MS = 250;
/**
 * y-partyserver turns off Yjs' built-in awareness heartbeat (to let rooms hibernate), so an idle user is
 * never re-announced and a vanished one is never timed out. We add a cheap heartbeat (1 message / 20 s)
 * and drop anyone silent for longer than PEER_TIMEOUT_MS.
 */
const HEARTBEAT_MS = 20_000;
const PEER_TIMEOUT_MS = 65_000;

/**
 * Idle disconnect. Every open connection keeps the room's Durable Object awake, and that is billed by the hour —
 * a forgotten tab would cost as much as a planning session. So the connection is paused when the tab has been
 * hidden for `hiddenMs`, or when there was no mouse/keyboard input for `inactiveMs`, and resumes by itself on the
 * next input or when the tab is shown again. Edits in between are kept locally and sync on resume.
 * (Mutable so tests can shorten it: `window.__mcp.idle`.)
 */
export const IDLE = { hiddenMs: 5 * 60_000, inactiveMs: 10 * 60_000, checkMs: 15_000 };
const ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"] as const;

/**
 * One planning document per world. Local-first: always persisted to IndexedDB;
 * optionally synced live through the MC Planner server (Cloudflare Durable Object, y-partyserver protocol).
 */
export class PlanSession {
  readonly doc = new Y.Doc();
  private readonly planStore: PlanStore;
  private readonly guardedStore: PlanStore;
  /** local document id (IndexedDB) — the world's id; the live room may have another id (LiveRoom.room) */
  readonly docId: string;
  /**
   * View only (viewer role, or a paused world): changes to the plan are ignored locally and dropped by the server.
   * `onBlocked` tells the person why nothing happened.
   */
  readOnly = false;
  onBlocked: () => void = () => {};
  onKicked: (reason: KickReason) => void = () => {};
  /** null = in-memory only (the marketing site's live demo: a fresh sandbox on every visit) */
  private idb: IndexeddbPersistence | null;
  private provider: YProvider | null = null;
  /** cursor / name-tag colour: the account's colour when signed in (setIdentity), else a random one */
  color = COLORS[Math.floor(Math.random() * COLORS.length)];
  /** account id shown to the others (see Peer.uid) */
  private uid: string | undefined;
  private lastCursorAt = 0;
  private pendingCursor: { x: number; y: number; z: number } | null | undefined;
  private cursorTimer = 0;
  onPeers: (p: Peer[]) => void = () => {};
  /** the server says the world's members or storage changed (someone joined, left, got a new role; files uploaded) */
  onRoomChanged: (what: "members" | "storage") => void = () => {};
  onStatus: (s: CollabStatus) => void = () => {};
  onSave: (s: SaveState) => void = () => {};
  saveState: SaveState = "local";
  /** local changes made while the live connection was down */
  private unsynced = false;
  private onPageHide = () => this.disconnect();
  private heartbeat = 0;
  /** what connect() was called with — kept while paused so the session can resume by itself */
  private live: { server: string; userName: string; room: LiveRoom } | null = null;
  private paused = false;
  private lastInput = Date.now();
  private hiddenSince: number | null = null;
  private idleTimer = 0;
  private onInput = () => {
    this.lastInput = Date.now();
    if (this.paused && !document.hidden) this.resume();
  };
  private onVisibility = () => {
    if (document.hidden) this.hiddenSince ??= Date.now();
    else { this.hiddenSince = null; this.onInput(); }
  };

  private constructor(docId: string, persist = true) {
    this.docId = docId;
    this.planStore = new PlanStore(this.doc);
    this.guardedStore = new Proxy(this.planStore, {
      get: (t, prop, recv) => {
        const v = Reflect.get(t, prop, recv);
        if (typeof v !== "function") return v;
        if (WRITES.has(prop as string)) return (...args: unknown[]) => (this.readOnly ? (this.onBlocked(), undefined) : (v as (...a: unknown[]) => unknown).apply(t, args));
        return (v as (...a: unknown[]) => unknown).bind(t);
      },
    });
    this.idb = persist ? new IndexeddbPersistence(`mc-planner:${docId}`, this.doc) : null;
    this.doc.on("update", (_u: Uint8Array, origin: unknown) => {
      const p = this.provider;
      if (!p || origin === p) return;
      if (!(p.wsconnected && p.synced)) { this.unsynced = true; this.setSave("offline"); }
    });
  }

  private setSave(s: SaveState) {
    if (s === this.saveState) return;
    this.saveState = s;
    this.onSave(s);
  }

  /** the plan (read-only wrapper while `readOnly`) */
  get store(): PlanStore {
    return this.guardedStore;
  }

  static async open(docId: string, opts: { persist?: boolean } = {}) {
    const s = new PlanSession(docId, opts.persist ?? true);
    if (s.idb) await s.idb.whenSynced;
    return s;
  }

  connect(server: string, userName: string, room: LiveRoom) {
    this.disconnect();
    this.live = { server, userName, room };
    this.watchIdle(true);
    this.openProvider(server, userName, room);
  }

  /**
   * Who I am to the others: the account's name + colour + id when signed in (one name everywhere: cursor, name tags,
   * the People list). Takes effect at once when live, and for every later (re)connect.
   */
  setIdentity(me: { name?: string; color?: string; uid?: string }) {
    if (me.color && /^#[0-9a-f]{6}$/i.test(me.color)) this.color = me.color;
    if (me.uid !== undefined) this.uid = me.uid || undefined;
    if (me.name && this.live) this.live = { ...this.live, userName: me.name };
    const aw = this.provider?.awareness;
    if (aw) aw.setLocalStateField("user", { name: this.live?.userName || "Anonymous", color: this.color, ...(this.uid ? { uid: this.uid } : {}) });
  }

  private openProvider(server: string, userName: string, room: LiveRoom) {
    this.paused = false;
    this.onStatus("connecting");
    let p: YProvider;
    let ended = false;
    const endFor = (reason: KickReason) => {
      if (ended) return;
      ended = true;
      setTimeout(() => {
        this.disconnect();
        this.onStatus("ended");
        this.onKicked(reason);
      }, 0);
    };
    try {
      p = new YProvider(serverHost(server), room.room, this.doc, {
        party: "plan-room",
        protocol: serverProtocol(server),
        disableBc: true,
        // a new ticket for every (re)connect: a changed role or a removal takes effect right away
        params: room.ticket
          ? async () => {
            try {
              return { ticket: await room.ticket!() };
            } catch (e) {
              const status = (e as { status?: number }).status ?? 0;
              if (status === 401 || status === 403 || status === 404) endFor(status === 404 ? "deleted" : "no-access");
              throw e;
            }
          }
          : room.hostKey ? { hostKey: room.hostKey } : undefined,
      });
    } catch {
      this.onStatus("error");
      return;
    }
    this.provider = p;
    p.on("status", ({ status }: { status: string }) => {
      if (ended || this.provider !== p) return;
      this.onStatus(status === "connected" ? "connected" : status === "connecting" ? "connecting" : "offline");
      if (status === "connected") this.setSave(p.synced ? "synced" : "syncing");
      else this.setSave(this.unsynced ? "offline" : "syncing");
    });
    p.on("sync", (synced: boolean) => {
      if (ended || this.provider !== p) return;
      if (synced) { this.unsynced = false; this.setSave("synced"); }
    });
    p.on("connection-error", () => { if (!ended && this.provider === p) this.onStatus("error"); });
    p.on("custom-message", (raw: string) => {
      let msg: { type?: string; ids?: number[] };
      try { msg = JSON.parse(raw); } catch { return; }
      if (msg.type === "peer-left" && Array.isArray(msg.ids)) {
        const gone = msg.ids.filter((id) => id !== p.awareness.clientID && p.awareness.getStates().has(id));
        if (gone.length) removeAwarenessStates(p.awareness, gone, "server");
      } else if (msg.type === "session-ended") {
        ended = true;
        this.disconnect();
        this.onStatus("ended");
      } else if (msg.type === "room-changed") {
        this.onRoomChanged((msg as { what?: string }).what === "storage" ? "storage" : "members");
      } else if (msg.type === "kicked") {
        const reason = (msg as { reason?: string }).reason;
        // a new role / the owner's new plan (paused or live) arrives with the next ticket: the provider reconnects by itself
        if (reason !== "role-changed" && reason !== "plan-changed") endFor((reason as KickReason) ?? "removed");
      }
    });
    window.addEventListener("pagehide", this.onPageHide);
    this.heartbeat = window.setInterval(() => {
      const aw = p.awareness;
      if (p.wsconnected) aw.setLocalStateField("t", Date.now()); // a *change*, so the provider sends it
      const now = Date.now();
      const stale: number[] = [];
      aw.meta.forEach((meta, id) => { if (id !== aw.clientID && aw.getStates().has(id) && now - meta.lastUpdated > PEER_TIMEOUT_MS) stale.push(id); });
      if (stale.length) removeAwarenessStates(aw, stale, "timeout");
    }, HEARTBEAT_MS);
    p.awareness.setLocalStateField("user", { name: userName || "Anonymous", color: this.color, ...(this.uid ? { uid: this.uid } : {}) });
    p.awareness.on("change", () => this.emitPeers());
  }

  private emitPeers() {
    const aw = this.provider?.awareness;
    if (!aw) return this.onPeers([]);
    const peers: Peer[] = [];
    aw.getStates().forEach((st, id) => {
      if (id === aw.clientID) return;
      const u = (st as { user?: { name: string; color: string; uid?: string } }).user;
      if (!u) return;
      peers.push({ clientId: id, name: u.name, color: u.color, uid: typeof u.uid === "string" ? u.uid : undefined, cursor: (st as { cursor?: Peer["cursor"] }).cursor ?? null });
    });
    this.onPeers(peers);
  }

  setCursor(c: { x: number; y: number; z: number } | null) {
    if (!this.provider) return;
    this.pendingCursor = c;
    const wait = this.lastCursorAt + CURSOR_INTERVAL_MS - Date.now();
    if (wait <= 0) this.flushCursor();
    else if (!this.cursorTimer) this.cursorTimer = window.setTimeout(() => this.flushCursor(), wait);
  }

  private flushCursor() {
    this.cursorTimer = 0;
    if (!this.provider || this.pendingCursor === undefined) return;
    this.lastCursorAt = Date.now();
    this.provider.awareness.setLocalStateField("cursor", this.pendingCursor);
    this.pendingCursor = undefined;
  }

  // ---------------- idle pause ----------------

  private watchIdle(on: boolean) {
    clearInterval(this.idleTimer);
    this.idleTimer = 0;
    for (const e of ACTIVITY_EVENTS) window.removeEventListener(e, this.onInput);
    document.removeEventListener("visibilitychange", this.onVisibility);
    if (!on) return;
    this.lastInput = Date.now();
    this.hiddenSince = document.hidden ? Date.now() : null;
    for (const e of ACTIVITY_EVENTS) window.addEventListener(e, this.onInput, { passive: true });
    document.addEventListener("visibilitychange", this.onVisibility);
    this.idleTimer = window.setInterval(() => this.checkIdle(), IDLE.checkMs);
  }

  /** exposed for tests; normally called every IDLE.checkMs */
  checkIdle(now = Date.now()) {
    if (!this.provider || this.paused) return;
    if (document.hidden) this.hiddenSince ??= now;
    const hidden = this.hiddenSince !== null && now - this.hiddenSince >= IDLE.hiddenMs;
    if (hidden || now - this.lastInput >= IDLE.inactiveMs) this.pause();
  }

  /** close the connection but stay "live": resume() reconnects with the same settings */
  private pause() {
    if (!this.provider) return;
    this.closeProvider();
    this.paused = true;
    this.onStatus("idle");
    this.onPeers([]);
    this.setSave("local");
  }

  private resume() {
    if (!this.paused || !this.live) return;
    this.paused = false;
    this.lastInput = Date.now();
    this.openProvider(this.live.server, this.live.userName, this.live.room);
  }

  /** Host only: disconnect every guest and close the room until the host goes live again. */
  endSessionForEveryone() {
    const p = this.provider;
    if (!p) return;
    p.sendMessage(JSON.stringify({ type: "end-session" }));
    setTimeout(() => this.disconnect(), 150);
  }

  /** Leave the live session (also stops the idle pause/resume). */
  disconnect() {
    const wasLive = !!this.provider || this.paused;
    this.watchIdle(false);
    this.live = null;
    this.paused = false;
    this.closeProvider();
    if (wasLive) {
      this.onStatus("offline");
      this.onPeers([]);
    }
    this.unsynced = false;
    this.setSave("local");
  }

  private closeProvider() {
    window.removeEventListener("pagehide", this.onPageHide);
    clearInterval(this.heartbeat);
    const p = this.provider;
    if (!p) return;
    this.provider = null; // first, so events fired while it shuts down are ignored
    // tell others right away that our cursor is gone (instead of waiting for the 30 s timeout)
    try { removeAwarenessStates(p.awareness, [p.awareness.clientID], "local"); } catch { /* ignore */ }
    p.destroy();
  }

  destroy() {
    this.disconnect();
    this.idb?.destroy();
    this.doc.destroy();
  }
}

/** Stable id for a world, used as the plan document / collaboration room name. */
export async function worldIdFor(name: string, seed: string | undefined) {
  const data = new TextEncoder().encode(`${name}|${seed ?? ""}`);
  const h = new Uint8Array(await crypto.subtle.digest("SHA-256", data));
  return Array.from(h.slice(0, 8), (b) => b.toString(16).padStart(2, "0")).join("");
}
